import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Slide,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Shield,
  Landmark,
  Camera,
  Brain,
  CreditCard,
  Users,
  X,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import { useThemeContext } from '../../contexts/ThemeContext';

// ─── Agent icon mapping ────────────────────────────────────────────────
const AGENT_ICONS = {
  '01-sentinel': Shield,
  '02-ledger':   Landmark,
  '03-vision':   Camera,
  '04-cortex':   Brain,
  '05-vault':    CreditCard,
  '06-nexus':    Users,
};

// ─── Type-specific accent colours ──────────────────────────────────────
const TYPE_COLORS = {
  budget_alert:     { main: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)' },
  spending_anomaly: { main: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)' },
  insight:          { main: '#a855f7', bg: 'rgba(168, 85, 247, 0.12)' },
  trial_warning:    { main: '#f97316', bg: 'rgba(249, 115, 22, 0.12)' },
  security:         { main: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)' },
  receipt_nudge:    { main: '#06b6d4', bg: 'rgba(6, 182, 212, 0.12)' },
};

const DEFAULT_COLOR = { main: '#10b981', bg: 'rgba(16, 185, 129, 0.12)' };

// ─── Stagger delay per card (ms) ──────────────────────────────────────
const STAGGER_DELAY = 120;

// ─── Single insight card ───────────────────────────────────────────────
const InsightCard = React.memo(function InsightCard({
  notification,
  index,
  themeMode,
  isMobile,
  onDismiss,
  onAction,
  t,
}) {
  const { _id, agentId, type, title, message, actionUrl, actionType } = notification;

  const accent = TYPE_COLORS[type] || DEFAULT_COLOR;
  const AgentIcon = AGENT_ICONS[agentId] || Brain;

  const handleDismiss = useCallback(
    (e) => {
      e.stopPropagation();
      onDismiss(_id);
    },
    [_id, onDismiss],
  );

  const handleAction = useCallback(() => {
    onAction(actionUrl, actionType, _id);
  }, [actionUrl, actionType, _id, onAction]);

  return (
    <Slide direction="left" in mountOnEnter unmountOnExit timeout={{ enter: 350 + index * STAGGER_DELAY }}>
      <Box
        onClick={handleAction}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? 1.5 : 2,
          px: isMobile ? 1.5 : 2.5,
          py: isMobile ? 1.25 : 1.75,
          mb: 1.5,
          position: 'relative',
          background:
            themeMode === 'light'
              ? 'rgba(255, 255, 255, 0.85)'
              : 'rgba(255, 255, 255, 0.06)',
          backdropFilter: 'blur(20px) saturate(180%)',
          border:
            themeMode === 'light'
              ? '1px solid rgba(255, 255, 255, 0.9)'
              : '1px solid rgba(255, 255, 255, 0.12)',
          borderLeft: `3px solid ${accent.main}`,
          borderRadius: isMobile ? '12px' : '16px',
          cursor: 'pointer',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow:
            themeMode === 'light'
              ? '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.04)'
              : '0 1px 3px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.3)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow:
              themeMode === 'light'
                ? '0 4px 12px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.08)'
                : '0 4px 12px rgba(0,0,0,0.5), 0 8px 24px rgba(0,0,0,0.4)',
          },
        }}
      >
        {/* Agent icon */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: isMobile ? 36 : 42,
            height: isMobile ? 36 : 42,
            borderRadius: '12px',
            background: accent.bg,
            color: accent.main,
          }}
        >
          <AgentIcon size={isMobile ? 18 : 22} />
        </Box>

        {/* Text content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: themeMode === 'light' ? '#0f172a' : '#f8fafc',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: isMobile ? '0.8rem' : '0.875rem',
              lineHeight: 1.4,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              color: themeMode === 'light' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(248, 250, 252, 0.5)',
              lineHeight: 1.4,
              fontSize: isMobile ? '0.7rem' : '0.75rem',
              mt: 0.25,
            }}
          >
            {message}
          </Typography>
        </Box>

        {/* Action button */}
        {actionUrl && (
          <Button
            size="small"
            onClick={handleAction}
            sx={{
              flexShrink: 0,
              minWidth: 'auto',
              px: 1.5,
              py: 0.5,
              fontSize: '0.7rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '8px',
              color: accent.main,
              background: accent.bg,
              '&:hover': {
                background: accent.bg,
                filter: 'brightness(1.15)',
              },
            }}
          >
            {t('dashboard.insights.view', 'View')}
          </Button>
        )}

        {/* Dismiss button */}
        <IconButton
          size="small"
          onClick={handleDismiss}
          aria-label={t('dashboard.insights.dismiss', 'Dismiss')}
          sx={{
            flexShrink: 0,
            width: 28,
            height: 28,
            color: themeMode === 'light' ? 'rgba(15, 23, 42, 0.35)' : 'rgba(248, 250, 252, 0.35)',
            '&:hover': {
              color: themeMode === 'light' ? 'rgba(15, 23, 42, 0.7)' : 'rgba(248, 250, 252, 0.7)',
              background: themeMode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
            },
          }}
        >
          <X size={16} />
        </IconButton>
      </Box>
    </Slide>
  );
});

// ─── Empty state ───────────────────────────────────────────────────────
const EmptyState = React.memo(function EmptyState({ themeMode, t }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        py: 3,
        px: 2,
        background:
          themeMode === 'light'
            ? 'rgba(255, 255, 255, 0.85)'
            : 'rgba(255, 255, 255, 0.06)',
        backdropFilter: 'blur(20px) saturate(180%)',
        border:
          themeMode === 'light'
            ? '1px solid rgba(255, 255, 255, 0.9)'
            : '1px solid rgba(255, 255, 255, 0.12)',
        borderRadius: '16px',
        boxShadow:
          themeMode === 'light'
            ? '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.06)'
            : '0 1px 3px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.3)',
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          animation: 'pulseGlow 2.5s ease-in-out infinite',
          '@keyframes pulseGlow': {
            '0%, 100%': { opacity: 1, transform: 'scale(1)' },
            '50%': { opacity: 0.7, transform: 'scale(1.12)' },
          },
        }}
      >
        <Sparkles size={20} color="#10b981" />
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: themeMode === 'light' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(248, 250, 252, 0.55)',
          fontWeight: 500,
          fontSize: '0.85rem',
        }}
      >
        {t('dashboard.insights.empty', 'No alerts — your finances look great!')}
      </Typography>
    </Box>
  );
});

// ─── Main component ────────────────────────────────────────────────────
export default function InlineInsightCards() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notifications, dismissNotification, markAsRead } = useNotification();
  const { themeMode } = useThemeContext();

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // Filter top 3 unread high-priority notifications
  const topInsights = useMemo(
    () =>
      (notifications || [])
        .filter((n) => !n.read && ['high', 'urgent'].includes(n.priority))
        .slice(0, 3),
    [notifications],
  );

  const handleAction = useCallback(
    (actionUrl, actionType, id) => {
      markAsRead(id);
      if (actionType === 'dismiss' || !actionUrl) {
        dismissNotification(id);
      } else {
        navigate(actionUrl);
      }
    },
    [navigate, markAsRead, dismissNotification],
  );

  const handleSeeAll = useCallback(() => {
    navigate('/dashboard/insights');
  }, [navigate]);

  return (
    <Box sx={{ width: '100%' }}>
      {topInsights.length === 0 ? (
        <EmptyState themeMode={themeMode} t={t} />
      ) : (
        <>
          {topInsights.map((notification, index) => (
            <InsightCard
              key={notification._id}
              notification={notification}
              index={index}
              themeMode={themeMode}
              isMobile={isMobile}
              onDismiss={dismissNotification}
              onAction={handleAction}
              t={t}
            />
          ))}

          {/* "See all insights" link */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
            <Button
              size="small"
              endIcon={<ArrowRight size={14} />}
              onClick={handleSeeAll}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.78rem',
                color: '#a855f7',
                px: 1,
                borderRadius: '8px',
                '&:hover': {
                  background:
                    themeMode === 'light'
                      ? 'rgba(168, 85, 247, 0.08)'
                      : 'rgba(168, 85, 247, 0.12)',
                },
              }}
            >
              {t('dashboard.insights.seeAll', 'See all insights')}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
